import { Observable } from 'rxjs/Observable'
import { Subject } from 'rxjs/Subject'

import { Bar, HistoryMetadata, LibrarySymbolInfo } from '../types/datafeed-api'

import { Command as MT4Command, Event as MT4Message, MT4Connector } from '@bdswiss/mt4-connector'
import { GetChart, ChartData, ChartBar, ChartSecondsData } from '@bdswiss/mt4-connector'
import { removeServerOffset } from '../../../utils/common-utils'

import { store } from '../../../'
import { fulfillCandlesData } from '../../../actions/signalsActions'
import { resolution2Seconds } from '../utils'
import { timeout } from 'rxjs/operators'

export interface GetBarsResult {
  bars: Bar[]
  meta: HistoryMetadata
}

export interface TVChartBar extends Bar {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume?: number
}

// Define mapper to map MT4 Chart bar to JS API Chart bar
export function chartBarToTVChartBar({ ts, o, h, l, c, v }: ChartBar): TVChartBar {
  return {
    time: removeServerOffset(ts) * 1000,
    open: o,
    high: h,
    low: l,
    close: c,
    volume: v,
  }
}

export function convertBarsData(
  bars: ChartBar[],
  resolution: number,
  rangeEndDate: number
): TVChartBar[] {
  const tvBars: TVChartBar[] = []
  if (resolution < 86400) {
    for (const bar of bars) {
      if (rangeEndDate < removeServerOffset(bar.ts)) {
        break
      }
      const { ts, o, h, l, c, v } = bar
      tvBars.push({
        time: removeServerOffset(ts) * 1000,
        open: o,
        high: h,
        low: l,
        close: c,
        volume: v,
      })
    }
  } else {
    for (const bar of bars) {
      if (rangeEndDate < bar.ts) {
        break
      }
      const { ts, o, h, l, c, v } = bar
      tvBars.push({
        time: ts * 1000,
        open: o,
        high: h,
        low: l,
        close: c,
        volume: v,
      })
    }
  }
  return tvBars
}

export class HistoryProvider {
  private static instance: HistoryProvider
  private readonly mt4: MT4Connector
  private barsObservable: Observable<ChartData | ChartSecondsData>

  public constructor() {
    this.mt4 = MT4Connector.Instance
    this.barsObservable = this.InitBarsObservable()
  }

  public static get Instance() {
    return this.instance || (this.instance = new this())
  }

  public getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: string,
    rangeStartDate: number,
    rangeEndDate: number,
    firstDataRequest: boolean
  ): Promise<GetBarsResult> {
    const symbolName = symbolInfo.name
    const mt4Resolution = resolution2Seconds(resolution)
    let bars: Bar[] = []
    const meta: HistoryMetadata = {
      noData: true,
    }
    // console.log('GET', new Date(rangeStartDate).toISOString(), new Date(rangeEndDate).toISOString())
    return new Promise(
      (resolve: (result: GetBarsResult) => void, reject: (reason: string) => void) => {
        // subscribe to chart message
        this.barsObservable
          .take(1)
          .pipe(timeout(30000))
          .subscribe(
            (data) => {
              if (/^\d+S$/.test(resolution)) {
                const { data: barsData } = data as ChartSecondsData
                if (barsData) {
                  for (const bar of barsData) {
                    if (rangeEndDate < removeServerOffset(bar[0])) {
                      break
                    }
                    bars.push({
                      time: removeServerOffset(bar[0]) * 1000,
                      open: bar[1],
                      high: bar[1],
                      low: bar[1],
                      close: bar[1],
                    })
                  }
                }
              } else {
                const { data: barsData } = data as ChartData
                // console.log('GOT', new Date(barsData[0].ts*1000).toISOString(), new Date(barsData[barsData.length - 1].ts*1000).toISOString())
                if (barsData) {
                  bars = convertBarsData(barsData, mt4Resolution, rangeEndDate)
                }
              }
              // console.log('CHART', new Date(bars[0].time).toISOString(), new Date(bars[bars.length - 1].time).toISOString())

              if (bars.length) {
                meta.noData = false
              } else {
                reject('LoadBarsError')
                return
              }

              store.dispatch(
                fulfillCandlesData({
                  symbol: symbolName,
                  resolution: mt4Resolution,
                  bars: new Map(bars.map((b) => [b.time, b] as [number, Bar])),
                })
              )
              // console.log(new Date(bars[0].time).toISOString())
              // console.log(new Date(bars[bars.length - 1].time).toISOString())
              resolve({ bars, meta })
            },
            (error) => {
              reject(error.toString())
            }
          )

        // request chart
        const chart: GetChart = {
          resolution: mt4Resolution,
          symbol: symbolName,
        }
        this.mt4.sendCommand(MT4Command.GET_CHART, chart)
      }
    )
  }

  private InitBarsObservable(): Observable<ChartData | ChartSecondsData> {
    const barsSubject = new Subject()
    this.mt4.onMessage(MT4Message.CHART).subscribe(barsSubject)
    return barsSubject.asObservable() as Observable<ChartData | ChartSecondsData>
  }
}
